import { useEffect, useCallback } from 'react';

const FOCUSABLE_SELECTOR = [
    'a[href]:not([disabled]):not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Enables F6 / Shift+F6 keyboard shortcut to cycle focus between
 * landmark regions (sidebar nav ↔ main content) — mimicking screen
 * reader landmark navigation for all keyboard users.
 *
 * Unlike focusing the container itself (which can cause Tab to jump
 * back to the DOM start), this hook always places focus on the FIRST
 * interactive child element inside the target landmark. This ensures
 * that subsequent Tab presses continue naturally within that region.
 *
 * The hook is inactive when:
 *   - A modal dialog is open (focus trap handles navigation)
 *   - Focus is inside a text input (F6 would disrupt typing)
 */
export function useLandmarkNavigation() {
    const getLandmarks = useCallback((): HTMLElement[] => {
        const landmarks: HTMLElement[] = [];

        // 1. Sidebar navigation (<nav> inside <aside>)
        const sidebarNav = document.querySelector<HTMLElement>(
            'aside nav[aria-label]'
        );
        if (sidebarNav) landmarks.push(sidebarNav);

        // 2. Main content area
        const main = document.getElementById('main-content');
        if (main) landmarks.push(main);

        return landmarks;
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'F6') return;

            // Don't interfere when a modal is open (focus trap handles navigation)
            if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;

            // Don't interfere with text input fields
            const active = document.activeElement;
            if (
                active instanceof HTMLInputElement ||
                active instanceof HTMLTextAreaElement
            ) {
                return;
            }

            e.preventDefault();

            const landmarks = getLandmarks();
            if (landmarks.length === 0) return;

            // Find which landmark currently contains focus
            const currentIndex = landmarks.findIndex((el) =>
                el.contains(document.activeElement)
            );

            // Calculate next index (forward with F6, backward with Shift+F6)
            let nextIndex: number;
            if (e.shiftKey) {
                nextIndex =
                    currentIndex <= 0 ? landmarks.length - 1 : currentIndex - 1;
            } else {
                nextIndex =
                    currentIndex >= landmarks.length - 1 ? 0 : currentIndex + 1;
            }

            const target = landmarks[nextIndex];

            // Always focus the first interactive child element inside the
            // target landmark. This ensures Tab continues naturally within
            // the region instead of jumping back to the DOM start.
            // setTimeout(0) is required for Safari/WebKit which ignores
            // synchronous .focus() on non-input elements during keydown.
            const firstFocusable = target.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
            if (firstFocusable) {
                setTimeout(() => firstFocusable.focus(), 0);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [getLandmarks]);
}
